/**
 * <StepLayout /> — shared chrome for every M27 step page.
 *
 * Provides:
 *   - PageHeader with breadcrumbs (Setup > {step name})
 *   - The "Step N of 9" indicator
 *   - Slot for the AI callout
 *   - Slot for body content
 *   - Sticky footer with Skip / Continue actions wired to
 *     useOnboardingAutoSave
 *
 * Pages compose this shell. Step-specific UI lives in `children`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ChevronLeft, SkipForward } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ErrorPanel, Loading } from "../../components/Loading";
import { PageHeader } from "../../components/ui";
import { api } from "../../lib/api";
import {
  NON_SKIPPABLE,
  ONBOARDING_JOURNEY_QUERY_KEY,
  STEP_SLUGS,
  type OnboardingState,
  type StepSlug,
} from "../../lib/onboarding";

const _STEP_LABEL_KEYS: Record<StepSlug, string> = {
  welcome: "app.onb_step_welcome",
  company: "app.onb_step_company",
  policies: "app.onb_step_policies",
  "jobs-grades": "app.onb_step_jobs_grades",
  employees: "app.onb_step_employees",
  approvals: "app.onb_step_approvals",
  integrations: "app.onb_step_integrations",
  "shadow-payroll": "app.onb_step_shadow_payroll",
  "go-live": "app.onb_step_go_live",
};
import { useOnboardingAutoSave } from "../../lib/use-onboarding-autosave";

export interface StepLayoutProps {
  slug: StepSlug;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Override the default Continue label. */
  continueLabel?: string;
  /** Custom click handler. Default: mark complete + advance. */
  onContinue?: () => Promise<void> | void;
  /** Disable Continue while validation fails / required work pending. */
  continueDisabled?: boolean;
  /** Render the Continue button at all. Step 9 uses its own. */
  hideContinue?: boolean;
}

export function StepLayout({
  slug,
  title,
  subtitle,
  children,
  continueLabel,
  onContinue,
  continueDisabled = false,
  hideContinue = false,
}: StepLayoutProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const auto = useOnboardingAutoSave(slug);

  const q = useQuery({
    queryKey: ONBOARDING_JOURNEY_QUERY_KEY,
    queryFn: () => api.getOnboardingJourneyState(),
  });

  const idx = STEP_SLUGS.indexOf(slug);
  const isLast = idx === STEP_SLUGS.length - 1;
  const isFirst = idx === 0;
  const nextSlug = isLast ? null : STEP_SLUGS[idx + 1];
  const prevSlug = isFirst ? null : STEP_SLUGS[idx - 1];
  const canSkip = !NON_SKIPPABLE.has(slug);

  const advance = useMutation({
    mutationFn: async () => {
      if (onContinue) await onContinue();
      await auto.markStatus("completed");
      if (nextSlug) {
        const next = await api.updateOnboardingJourneyState({
          current_step: nextSlug,
        });
        qc.setQueryData(ONBOARDING_JOURNEY_QUERY_KEY, next);
      }
    },
    onSuccess: () => {
      if (nextSlug) navigate(`/onboarding/${nextSlug}`);
      else navigate("/onboarding");
    },
  });

  const skip = useMutation({
    mutationFn: async () => {
      await auto.markStatus("skipped", { skipped_reason: "user_choice" });
      if (nextSlug) {
        const next = await api.updateOnboardingJourneyState({
          current_step: nextSlug,
        });
        qc.setQueryData(ONBOARDING_JOURNEY_QUERY_KEY, next);
      }
    },
    onSuccess: () => {
      if (nextSlug) navigate(`/onboarding/${nextSlug}`);
      else navigate("/onboarding");
    },
  });

  // Mark "in_progress" on first visit if not already.
  if (q.data && q.data.steps?.[slug]?.status === "not_started") {
    void auto.markStatus("in_progress");
  }

  if (q.isLoading) return <Loading />;
  if (q.error)
    return <ErrorPanel message={(q.error as Error).message} />;
  const state = q.data as OnboardingState;

  return (
    <div className="flex flex-col gap-5 pb-24">
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumbs={[
          { label: t("app.onb_journey_breadcrumb"), href: "/onboarding" },
          { label: t(_STEP_LABEL_KEYS[slug]) },
        ]}
      />

      <div className="flex items-center gap-3 text-xs text-text-tertiary">
        <span className="rounded-full border border-border-subtle bg-surface-default px-2 py-0.5 font-medium">
          {t("app.onb_step_n_of", { n: idx + 1, total: STEP_SLUGS.length })}
        </span>
        {state.steps?.[slug]?.last_activity_at && (
          <span>
            {t("app.onb_last_saved", {
              time: new Date(state.steps[slug].last_activity_at!).toLocaleTimeString(),
            })}
          </span>
        )}
      </div>

      <div>{children}</div>

      {/* Sticky footer — opaque white. Explicit inline
          background-color so the bar reads as a solid surface
          regardless of any radial-gradient washes on hero cards
          above it (the previous bg-surface-default class was
          resolving correctly but looked tinted against gradient
          step pages — explicit #ffffff removes ambiguity). */}
      <footer
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border-subtle px-4 py-3 shadow-lg"
        style={{
          marginInlineEnd: "var(--chat-panel-width, 0px)",
          backgroundColor: "#ffffff",
        }}
      >
        <div className="mx-auto flex max-w-workspace items-center gap-2">
          {prevSlug && (
            <button
              type="button"
              onClick={() => navigate(`/onboarding/${prevSlug}`)}
              className="inline-flex items-center gap-1 rounded border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
            >
              <ChevronLeft size={14} aria-hidden className="rtl:rotate-180" /> {t("app.onb_back")}
            </button>
          )}
          {canSkip && (
            <button
              type="button"
              onClick={() => skip.mutate()}
              disabled={skip.isPending}
              className="inline-flex items-center gap-1 rounded border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken disabled:opacity-50"
            >
              <SkipForward size={14} aria-hidden /> {t("app.onb_skip_for_now")}
            </button>
          )}
          <span className="ms-auto" />
          {!hideContinue && (
            <button
              type="button"
              onClick={() => advance.mutate()}
              disabled={advance.isPending || continueDisabled}
              className="inline-flex items-center gap-1 rounded bg-brand px-4 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
            >
              {advance.isPending
                ? t("app.onb_saving")
                : (continueLabel ??
                  (isLast ? t("app.onb_finish") : t("app.onb_continue_btn")))}
              <ArrowRight size={14} aria-hidden className="rtl:rotate-180" />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
